<template>
  <div>

    <c-busy v-if="loadStatus === $config.status.LOADING" />

    <template v-else-if="loadStatus === $config.status.SUCCESS">

      <c-progress
        v-if="workflowConfig && blockConfigs && steps && currentStep >= 0"
        :current="currentStep"
        :steps="steps"
        :key="valCount"
      />

      <c-title v-if="blockConfig">{{ blockConfig.config.meta.title }}</c-title>

      <c-form
        v-if="blockConfig"
        @submit.prevent
      >

        <c-process-block
          v-for="i in blockCount"
          :key="`${blockConfig.name}-${i}`"
          :name="blockConfig.name"
          :config="blockConfig.config"
          :validator="validator"
          :block-index="i - 1"
          :workflow="workflowName"
          :process-id="process.identifier"
        />

        <p class="space-top">

          <c-button
            v-if="blockCount < blockConfig.config.meta.max_amount"
            @click="increaseBlockCount"
          >
            {{ blockConfig.config.meta.button_title || 'Weiteres Dokument' }}
            hinzufügen
          </c-button>

          <c-button
            v-if="blockCount > 1"
            @click="decreaseBlockCount"
          >
            {{ blockConfig.config.meta.button_title || 'Dokument' }}
            entfernen
          </c-button>

        </p>

        <div
          v-if="steps"
          class="button-group"
        >

          <c-button
            v-if="currentStep > 0"
            @click="previousStep"
          >
            Zurück zu {{ steps[currentStep - 1].title }}
          </c-button>

          <c-button
            v-if="currentStep < steps.length - 1"
            class="button--right"
            solid
            @click="nextStep"
          >
            Weiter zu {{ steps[currentStep + 1].title }}
          </c-button>

          <c-button
            v-if="currentStep === steps.length - 1"
            class="button--right"
            solid
            @click="submit"
          >
            Bewerbung absenden
          </c-button>

        </div>

      </c-form>
    </template>
  </div>
</template>

<script>
import CBusy from '@/components/CBusy'
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CProcessBlock from '@/components/process/CProcessBlock'
import CProgress from '@/components/CProgress'
import CTitle from '@/components/CTitle'

import config from '@/config'
import eventBus from '@/functions/eventBus'
import handleRequestError from '@/functions/handleRequestError'
import processValidator from '@/functions/processValidator'

export default {
  name: 'VFormRenderer',

  components: {
    CBusy,
    CButton,
    CForm,
    CProcessBlock,
    CProgress,
    CTitle
  },

  props: {
    identifier: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loadStatus: this.$config.status.IDLE,
      workflowName: '',
      validator: processValidator,
      process: {},
      currentStep: -1,
      workflowConfig: null,
      blockConfigs: [],
      blockMetaInformation: null,
      blockCount: 1,
      steps: null,
      valCount: 0
    }
  },

  computed: {

    blockCountId () {
      if (this.workflowName && this.blockConfig && this.currentStep >= 0) {
        return this.workflowName + '_' + this.blockConfig.name + '_' + this.currentStep
      }

      return ''
    },

    blockConfig () {
      if (this.blockConfigs.hasOwnProperty(this.currentStep)) {
        return this.blockConfigs[parseInt(this.currentStep)]
      } else {
        return false
      }
    }
  },

  watch: {
    currentStep (value, oldValue) {
      if (value !== oldValue) {
        this.validateBlock(oldValue)
        this.updateStepsStatus()

        const h1 = document.querySelector('h1')
        if (h1) {
          h1.focus()
        }

        // Update block count
        let blockData = this.$store.state.data['blocks']
        if (blockData.hasOwnProperty(this.workflowName) && blockData[this.workflowName].hasOwnProperty(this.blockConfig.name)) {
          this.blockCount = Object.keys(blockData[this.workflowName][this.blockConfig.name]).length
        } else {
          this.blockCount = 1
        }
      }
    },

    blockCount (val) {
      this.$store.commit('data/setBlockCount', {
        blockCountId: this.blockCountId,
        value: val
      })
    }
  },

  created () {
    this.loadStatus = this.$config.status.LOADING

    let processIdentifier = this.$route.params['identifier']
    this.$api.get('workflow/process/' + processIdentifier + '/')
      .then(response => {
        this.$store.commit('data/setData', {
          root: response.data.workflow.name,
          data: response.data.data
        })

        this.process = response.data
        this.loadWorkflow(this.process.workflow.name)
      })
      .catch(error => this.handleLoadError(error))

    eventBus.$on('validateBlock', () => {
      this.validateBlock(this.currentStep)
    })
  },

  methods: {
    handleLoadError (error, fallbackMessage = 'Ihr Förderprogramm konnten nicht geladen werden. Bitte versuchen sie es später erneut.') {
      handleRequestError({
        error,
        fallback: fallbackMessage
      })

      this.$router.push({ name: 'dashboard' })
    },

    validateBlock (blockIndex) {
      if (!this.blockConfigs.hasOwnProperty(blockIndex)) {
        return true
      }

      let blockConfig = this.blockConfigs[blockIndex]
      let blockName = blockConfig.name
      let blockValues = this.$store.state.data.blocks[this.workflowName][blockName]

      this.validator.validateBlock(blockName, blockValues, blockConfig.config)
      this.updateStepsStatus()
    },

    validateAllBlocks () {
      for (let blockIndex in this.blockConfigs) {
        this.validateBlock(blockIndex)
      }

      this.updateStepsStatus()
      return !this.validator.hasAnyErrors()
    },

    updateStepsStatus () {
      for (let stepIndex in this.steps) {
        if (!this.steps.hasOwnProperty(stepIndex)) {
          continue
        }

        this.steps[stepIndex].status = this.validator.blockHasErrors(this.steps[stepIndex].blockName) ? config.status.ERROR : config.status.NONE
      }

      this.valCount++
    },

    loadWorkflow (workflowName) {
      this.workflowName = workflowName
      this.$store.dispatch('data/addWorkflow', workflowName)

      this.$api.get('workflow/workflow/' + workflowName + '/')
        .then(response => {
          this.workflowConfig = response.data
          this.loadBlockConfigs(this.workflowConfig.blocks)
        })
        .catch(error => this.handleLoadError(error))
    },

    loadBlockConfigs (blocks) {
      this.$api.get('workflow/block/' + blocks.join(',') + '/complete/')
        .then(response => {
          this.blockConfigs = response.data
          this.steps = []

          for (let blockIndex in this.blockConfigs) {
            if (!this.blockConfigs.hasOwnProperty(blockIndex)) {
              return Promise.reject(new Error('Das Förderprogramm ist nicht korrekt eingerichtet!!!'))
            }

            let blockConfig = this.blockConfigs[blockIndex]
            this.validateBlock(blockIndex)

            const stepConfig = {
              title: blockConfig.config.meta.title,
              action: () => { this.currentStep = parseInt(blockIndex) },
              blockName: blockConfig.name,
              status: this.validator.blockHasErrors(blockConfig.name) ? config.status.ERROR : config.status.NONE
            }

            this.steps.push(stepConfig)
          }

          this.currentStep = 0
          this.loadStatus = this.$config.status.SUCCESS
        })
        .catch(error => this.handleLoadError(error))
    },

    previousStep () {
      this.currentStep -= 1
    },

    nextStep () {
      this.currentStep += 1
    },

    increaseBlockCount () {
      this.blockCount++
    },

    decreaseBlockCount () {
      if (this.blockCount > 1) {
        this.blockCount--
        let blockId = 'bi_' + this.blockCount

        this.$store.dispatch('data/removeBlock', {
          workflow: this.workflowName,
          blockName: this.blockConfig.name,
          blockIndex: blockId
        })

        this.validateBlock(this.currentStep)

        this.$api.post('workflow/process/' + this.process.identifier + '/remove_block/', {
          block_name: this.blockConfig.name,
          block_index: blockId
        }).then(response => {
          eventBus.$emit('blockSaved', blockId)
        }).catch({
          // Do nothing
        })
      }
    },

    submit () {
      if (!this.validateAllBlocks()) {
        this.$store.commit('modal/open', {
          title: 'Bewerbung',
          content: 'Ihre Bewerbung konnte nicht abgeschickt werden, da wir noch Fehler gefunden haben. Bitte kontrollieren Sie Ihre Eingaben.',
          buttons: [{
            text: 'Ok'
          }]
        })

        return
      }

      this.$api.get('workflow/process/' + this.process.identifier + '/finish/')
        .then(response => {
          this.$store.commit('modal/open', {
            title: 'Bewerbung',
            content: 'Ihre Bewerbung wurde erfolgreich übermittelt. Wir werden uns bei Ihnen melden.',
            buttons: [{
              text: 'Ok',
              handler: () => { this.$router.push({ name: 'dashboard' }) }
            }]
          })
        })
        .catch(error => {
          let buttons = [{ text: 'Ok' }]
          let msg = error.response.data
          if (error.response.data === 'workflow_ended') {
            msg = 'Der Bewerbungszeitraum ist abgelaufen'
          } else if (error.response.data === 'other_running_processes') {
            msg = 'Sie haben bereits eine laufende Bewerbung.'
          } else {
            buttons = [{ text: 'Nein' }, { text: 'Ja', handler: () => this.submit() }]
          }

          this.$store.commit('modal/open', {
            title: 'Fehler',
            content: 'Es ist ein Fehler aufgetreten:' + msg,
            buttons: buttons
          })
        })
    }
  }
}
</script>

<style scoped="true">
.space-top {
  margin-top: 2rem;
}

.button-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
}

.button--right,
.button + .button--right {
  margin-left: auto;
}
</style>
